import { Checkbox, Stack } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import * as state from '@/components/Analysis/state';
import { ToggleableDefectLegend } from '@/types';
import { shouldDisableCleanDefect } from '@/utils/defects';

type Props = ToggleableDefectLegend;

export const DefectsToggle = ({ id, name, isEnabled, color, groupName }: Props) => {
  const setBlendingOpacity = useSetAtom(state.blendOpacity);
  const [selectedStructureDefectsLegends, setSelectedStructureDefectsLegends] = useAtom(
    state.selectedStructureDefectsLegends
  );
  const hasRgb = useAtomValue(state.hasRgb);
  const handleCheckboxChange = useCallback(
    (objectId: number) => {
      if (!selectedStructureDefectsLegends) return;

      const newDefects = selectedStructureDefectsLegends.map((defect) => {
        if (shouldDisableCleanDefect(defect.name)) {
          return {
            ...defect,
            isEnabled: false,
          };
        }
        if (defect.id === objectId) {
          return {
            ...defect,
            isEnabled: !defect.isEnabled,
          };
        }
        if (defect.groupName !== groupName) {
          return {
            ...defect,
            isEnabled: false,
          };
        }
        return defect;
      });
      setSelectedStructureDefectsLegends(newDefects);
      const anySelected = newDefects.some((defect) => defect.isEnabled);
      if (hasRgb) setBlendingOpacity(anySelected ? 255 : 1);
    },
    [
      groupName,
      hasRgb,
      selectedStructureDefectsLegends,
      setBlendingOpacity,
      setSelectedStructureDefectsLegends,
    ]
  );

  const colorBoxStyles = useMemo(
    () => ({
      backgroundColor: color,
      height: '2rem',
      width: '2rem',
      borderRadius: '10px',
      border: '1px solid black',
    }),
    [color]
  );

  return (
    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
      <Stack direction="row" alignItems="center" className="defect-toggle-container">
        <Checkbox
          checked={isEnabled}
          onChange={() => handleCheckboxChange(id)}
          className="defect-toggle"
          inputProps={{ 'aria-label': `Toggle ${name} defect` }}
        />
        <div>{name}</div>
      </Stack>
      <div style={colorBoxStyles} />
    </Stack>
  );
};
